import { groupOrderSetFiles } from "./group";
import type {
  ClassifiedOrderSetFile,
  OrderSetFileInput,
  OrderSetLanguageLayer,
} from "./types";

/**
 * One selection action loads a whole serial. A serial is three things: the
 * warning order, the orders, and the trace image.
 *
 * Loading is one action. Delivery is not, and nothing here starts one.
 */
export interface ResolvedOrderSet {
  readonly languageLayer: OrderSetLanguageLayer;
  readonly ordersCard: ClassifiedOrderSetFile;
  readonly setId: string;
  readonly setKey: string;
  readonly traceImage: ClassifiedOrderSetFile;
  /**
   * Null when the serial has no warning order.
   *
   * Battle procedure puts the warning order first, so a set that has one is
   * delivered warning order then orders. Not every serial has one, and by
   * Ruling 45 of 20 September 2026 such a set is not refused: delivery
   * starts at the orders and the panel says so.
   */
  readonly warningOrder: ClassifiedOrderSetFile | null;
}

export type OrderSetRefusalCode =
  | "AMBIGUOUS_ROLE"
  | "INCOMPLETE"
  | "MIXED_LANGUAGE"
  | "MULTIPLE_SETS"
  | "NO_RECOGNISED_FILES";

export interface OrderSetRefusal {
  readonly code: OrderSetRefusalCode;
  /** Plain language, shown to the trainee as-is. */
  readonly message: string;
  readonly ok: false;
}

export type OrderSetIntakeResult =
  | OrderSetRefusal
  | { readonly ok: true; readonly set: ResolvedOrderSet };

function refuse(code: OrderSetRefusalCode, message: string): OrderSetRefusal {
  return { ok: false, code, message };
}

function missingParts(candidate: {
  readonly ordersCard: ClassifiedOrderSetFile | null;
  readonly traceImage: ClassifiedOrderSetFile | null;
  readonly warningOrder: ClassifiedOrderSetFile | null;
}): readonly string[] {
  const missing: string[] = [];
  // The warning order is not listed. A serial without one is delivered from
  // the orders; only the orders and the trace are indispensable.
  if (candidate.ordersCard === null) missing.push("the orders");
  if (candidate.traceImage === null) missing.push("the trace image");
  return missing;
}

/**
 * Resolves one selection into a single complete serial, or refuses it with a
 * reason the trainee can act on.
 *
 * A set is one language. The completed sets hold both languages side by side
 * as separate serials with different grids and different timings, so a mixed
 * load produces orders that sound correct and are not. It is refused rather
 * than reconciled.
 *
 * An incomplete set is refused outright. Nothing is substituted and delivery
 * never proceeds on two of three.
 */
export function resolveOrderSetSelection(
  supplied: readonly (OrderSetFileInput | string)[],
): OrderSetIntakeResult {
  const grouping = groupOrderSetFiles(supplied);

  if (grouping.sets.length === 0) {
    return refuse(
      "NO_RECOGNISED_FILES",
      "No order set was recognised in that selection. Select the warning order, the orders and the trace image together.",
    );
  }

  const setIds = [...new Set(grouping.sets.map(({ setId }) => setId))];
  if (setIds.length > 1) {
    return refuse(
      "MULTIPLE_SETS",
      `That selection spans ${String(setIds.length)} different order sets. Select the three files of one set only.`,
    );
  }

  // A document layer is its own serial, so more than one means a mixed load.
  const documentLayers = grouping.sets.filter(
    (candidate) =>
      candidate.warningOrder !== null || candidate.ordersCard !== null,
  );
  const layers = [
    ...new Set(documentLayers.map(({ languageLayer }) => languageLayer)),
  ];
  if (layers.length > 1) {
    return refuse(
      "MIXED_LANGUAGE",
      "That selection mixes languages. The English and French halves of a set are separate serials with different grids and different timings, so they must never be delivered together. Select one language only.",
    );
  }

  const [candidate] =
    documentLayers.length > 0 ? documentLayers : grouping.sets;
  /* v8 ignore next 2 -- an empty grouping already returned above */
  if (candidate === undefined) {
    return refuse("NO_RECOGNISED_FILES", "No order set was recognised.");
  }

  if (
    grouping.issues.some(
      ({ code, setKey }) =>
        code === "AMBIGUOUS_ROLE" && setKey === candidate.setKey,
    )
  ) {
    return refuse(
      "AMBIGUOUS_ROLE",
      "Two files in that selection claim the same role. Select exactly one warning order, one orders document and one trace image.",
    );
  }

  const missing = missingParts(candidate);
  if (missing.length > 0) {
    return refuse(
      "INCOMPLETE",
      `That set is incomplete: ${missing.join(" and ")} ${missing.length === 1 ? "is" : "are"} missing. A serial is delivered whole or not at all.`,
    );
  }

  const { ordersCard, traceImage, warningOrder } = candidate;
  /* v8 ignore next 3 -- missingParts already proved both are present */
  if (ordersCard === null || traceImage === null) {
    return refuse("INCOMPLETE", "That set is incomplete.");
  }

  return {
    ok: true,
    set: {
      languageLayer: candidate.languageLayer,
      ordersCard,
      setId: candidate.setId,
      setKey: candidate.setKey,
      traceImage,
      warningOrder,
    },
  };
}
